import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { SlotContainer } from '@/app/components/search/components/EvolutionCard/Badges/styled.ts';
import { CARD_DESKTOP_ROUNDING } from '@/app/components/search/components/EvolutionCard/constants.ts';
import { focusRing, userSelectNone } from '../../../styles/mixins';
import { imageWidthCalcStyle, priceContainerWidthCalcStyle } from '../utils/styled';

export const DetailsContainer = styled.div<{ isOverlay?: boolean; isNotClickable?: boolean }>`
  width: 100%;
  background: white;
  display: grid;
  ${userSelectNone};

  ${({ isNotClickable }) =>
    isNotClickable &&
    css`
      cursor: default;
    `}

  ${({ isOverlay }) => {
    if (isOverlay) {
      return css`
        border-radius: 0 0 ${CARD_DESKTOP_ROUNDING} ${CARD_DESKTOP_ROUNDING};

        @media (max-width: 767px) {
          width: calc(100% - 104px);
          display: flex;
          flex-direction: column;
          border-radius: 0 ${CARD_DESKTOP_ROUNDING} ${CARD_DESKTOP_ROUNDING} 0;
        }
      `;
    }
    return css`
      border-radius: 0 0 ${CARD_DESKTOP_ROUNDING} ${CARD_DESKTOP_ROUNDING};

      @media (min-width: 768px) {
        display: flex;
        border-radius: 0 ${CARD_DESKTOP_ROUNDING} ${CARD_DESKTOP_ROUNDING} 0;

        ${imageWidthCalcStyle};
      }
    `;
  }}
`;

export const DetailsInnerContainer = styled.div<{
  isOverlay: boolean;
  soldOut?: boolean;
  isLoading?: boolean;
  isWaiting?: boolean;
}>`
  padding: 12px 16px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-width: 0;

  @media (min-width: 768px) {
    ${({ isOverlay, soldOut }) => !isOverlay && !soldOut && priceContainerWidthCalcStyle}
  }

  @media (max-width: 767px) {
    /* The price block below carries the bottom padding, so a sold-out card has to keep its own.
       This rule outranks the overlay shorthand below, so it repeats the overlay's own value. */
    padding-bottom: ${({ soldOut, isOverlay }) => (soldOut ? (isOverlay ? '12px' : '16px') : '0')};

    /* Except when the alternative dates block is there: it already ends on its own padding. */
    &:has(.alternative-dates-container) {
      padding-bottom: 0;
    }

    ${({ isOverlay }) =>
      isOverlay &&
      css`
        margin-bottom: 4px;
      `}
  }

  ${({ isOverlay, soldOut }) =>
    isOverlay
      ? css`
          padding: 8px 12px ${soldOut ? '12px' : '0'};
        `
      : css`
          @media (min-width: 768px) {
            padding-top: 16px;
          }
        `}
`;

export const TopText = styled(Text)`
  display: block;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  margin-bottom: 8px;

  @media (min-width: 768px) {
    margin-bottom: 16px;
  }

  img {
    width: 16px;
    height: 16px;
    margin-right: 4px;
    margin-top: 2px;
  }
`;

// The place (Costa Adeje · Tenerife), kept whole: it wraps inside itself only when it is wider than the card.
export const LocationPart = styled.span`
  display: inline-block;
`;

export const LocationSeparator = styled.span`
  margin: 0 4px;
`;

export const ViewOnMapButton = styled.button`
  display: inline;
  vertical-align: baseline;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  /* A button centres its text; the mobile distance button can wrap onto a second line, which has to start at the left. */
  text-align: start;
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-decoration-style: solid;
  text-decoration-thickness: 1px;
  text-decoration-color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  text-underline-offset: 2px;
`;

export const HotelNameLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    ${focusRing};
  }
`;

export const HotelName = styled(Text)<{ $isOverlay?: boolean; $isTSH?: boolean }>`
  margin-bottom: 2px;

  /* A gap after the name and after the stars, whichever of them is followed by something: the stars
     follow the name, the loyalty badge follows either, and an unrated property has no stars at all. */
  &:has(.stars) span,
  &:has(${SlotContainer}) span {
    margin-right: 8px;
  }

  @media (min-width: 768px) {
    margin-bottom: 4px;
  }

  ${({ $isOverlay, $isTSH }) => {
    if ($isTSH) {
      return css``;
    }

    if ($isOverlay) {
      return css`
        max-width: 100%;

        @media (max-width: 767px) {
          font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
        }
      `;
    }

    return css``;
  }}
`;
